import { combineReducers } from 'redux'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import {
    ByClientData,
    ByClientQuery,
    StatisticsData,
    StatisticsType
} from '../../../responses/statistics'
import * as O from 'fp-ts/Option'
import { Option } from 'fp-ts/Option'
import { RootEpic } from '../../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import { payloadReducer } from '../../../utils/redux-utils'
import { DateTuple } from '../../../components/data-components/date/DatePicker/DatePicker'
import { Brand, ClientWithBrands } from '../../../responses/refs/refs'
import { startOfMonth, endOfDay} from 'date-fns'
import {RequestStatus} from '../../../responses/response'

export const byClientActions = {
    data: createAsyncAction(
        '@STATISTICS_BY_CLIENT/DATA_REQUEST',
        '@STATISTICS_BY_CLIENT/DATA_SUCCESS',
        '@STATISTICS_BY_CLIENT/DATA_FAILURE',
    )<ByClientQuery, StatisticsData<ByClientData>, void>(),
    setType: createAction('@STATISTICS_BY_CLIENT/SET_TYPE')<StatisticsType>(),
    setDate: createAction('@STATISTICS_BY_CLIENT/SET_DATE')<DateTuple>(),
    setPeriods: createAction('@STATISTICS_BY_PERIODS/SET_PERIODS')<boolean>(),
    setClients: createAction('@STATISTICS_BY_CLIENTS/SET_CLIENTS')<ClientWithBrands[]>(),
    setBrands: createAction('@STATISTICS_BY_CLIENTS/SET_BRANDS')<Brand[]>(),
    clearFilters: createAction('@STATISTICS_BY_CLIENTS/CLEAR_FILTERS')(),
}

export interface ByClientState {
    data: Option<StatisticsData<ByClientData>>,
    type: StatisticsType,
    date: DateTuple,
    periods: boolean,
    clients: ClientWithBrands[],
    brands: Brand[],
    state: RequestStatus
}

export const byClientReducer = combineReducers<ByClientState>({
    data: createReducer<Option<StatisticsData<ByClientData>>>(O.none)
        .handleAction(byClientActions.data.success, (_, action) => O.of(action.payload)),
    type: createReducer<StatisticsType>('project-opened')
        .handleAction(byClientActions.setType, payloadReducer),
    date: createReducer<DateTuple>([startOfMonth(new Date()), endOfDay(new Date())])
        .handleAction(byClientActions.setDate, payloadReducer)
        .handleAction(byClientActions.clearFilters, () => [startOfMonth(new Date()), endOfDay(new Date())]),
    periods: createReducer(false)
        .handleAction(byClientActions.setPeriods, payloadReducer)
        .handleAction(byClientActions.clearFilters, () => false),
    clients: createReducer<ClientWithBrands[]>([])
        .handleAction(byClientActions.setClients, payloadReducer)
        .handleAction(byClientActions.clearFilters, () => []),
    brands: createReducer<Brand[]>([])
        .handleAction(byClientActions.setBrands, payloadReducer)
        .handleAction(byClientActions.clearFilters, () => []),
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(byClientActions.data.request, () => RequestStatus.FETCHING)
        .handleAction(byClientActions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(byClientActions.data.failure, () => RequestStatus.FAILURE),
})

export const byClientEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byClientActions.data.request)),
    mergeMap(action => apiRequests.statistics.byClient({query: action.payload})),
    map(({response}) => response.success ?
        byClientActions.data.success(response) :
        byClientActions.data.failure(toastFailedResponse(response, 'Не удалось запросить данные диаграммы'))
    ),
)